import { getOrder, listOrders } from "./orders";
import { queryOptions } from "@tanstack/react-query";
import {
  categoriesFromMenu,
  getBranches,
  getCompactMenu,
  getDeliveryAreas,
  getMenuItemBySlug,
  menuItemsFromMenu,
  type MenuItemQuery,
} from "./api";
import type { ServiceType } from "@emynor/storefront-sdk";
import { storefrontBootstrap, storefrontSdk } from "./sdk";
import { shouldRetryStorefrontQuery } from "./customer-error";

export const STOREFRONT_BOOTSTRAP_REFRESH_MS = 60_000;

export const storefrontBootstrapQuery = () =>
  queryOptions({
    queryKey: ["storefront-bootstrap"],
    queryFn: ({ signal }) => storefrontBootstrap(signal),
    staleTime: STOREFRONT_BOOTSTRAP_REFRESH_MS,
    refetchInterval: STOREFRONT_BOOTSTRAP_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetryStorefrontQuery,
  });

const catalogueFreshness = {
  staleTime: 30_000,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: true,
};

export function saleRefreshInterval(
  nextChangeAt: string | null | undefined,
  now = Date.now(),
): number | false {
  if (!nextChangeAt) return false;
  const remaining = Date.parse(nextChangeAt) - now;
  if (!Number.isFinite(remaining)) return false;
  return Math.max(250, Math.min(remaining + 100, 86_400_000));
}

export const branchesQuery = () =>
  queryOptions({
    queryKey: ["branches"],
    queryFn: ({ signal }) => getBranches(signal),
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

export const publicBranchMenuQuery = (
  branchId: string,
  onlineEnabled: boolean,
  indoorEnabled: boolean,
) =>
  queryOptions({
    queryKey: ["public-branch-menu", branchId, onlineEnabled, indoorEnabled],
    queryFn: async ({ signal }) => {
      const primaryType = onlineEnabled
        ? ("pickup" as const)
        : ("indoor" as const);
      const primary = await getCompactMenu(branchId, primaryType, signal);
      if (primary.items.length || primaryType === "indoor" || !indoorEnabled) {
        return { data: primary, orderType: primaryType };
      }
      return {
        data: await getCompactMenu(branchId, "indoor", signal),
        orderType: "indoor" as const,
      };
    },
    enabled: Boolean(branchId) && (onlineEnabled || indoorEnabled),
    staleTime: 30_000,
    refetchInterval: (query) =>
      saleRefreshInterval(query.state.data?.data.nextSaleChangeAt),
  });

const compactMenuQuery = (branchId: string, orderType: ServiceType) =>
  queryOptions({
    queryKey: ["compact-menu", branchId, orderType] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getCompactMenu(branchId, orderType, signal),
    enabled: Boolean(branchId),
    refetchInterval: (query) =>
      saleRefreshInterval(query.state.data?.nextSaleChangeAt),
    ...catalogueFreshness,
  });

export const categoriesQuery = (branchId: string, orderType: ServiceType) =>
  queryOptions({
    ...compactMenuQuery(branchId, orderType),
    select: categoriesFromMenu,
  });

export const menuItemsQuery = (q: MenuItemQuery) =>
  queryOptions({
    ...compactMenuQuery(q.branchId ?? "", q.orderType),
    select: (menu) => menuItemsFromMenu(menu, q),
  });

export const categoryItemsQuery = (
  categoryId: string,
  branchId: string,
  orderType: "delivery" | "pickup",
) =>
  queryOptions({
    ...compactMenuQuery(branchId, orderType),
    enabled: Boolean(branchId && categoryId),
    select: (menu) =>
      menuItemsFromMenu(menu, {
        branchId,
        orderType,
        categoryId,
        limit: menu.items.length,
      }).data,
  });

export const checkoutSuggestionsQuery = (
  branchId: string,
  orderType: "delivery" | "pickup",
) =>
  queryOptions({
    ...compactMenuQuery(branchId, orderType),
    select: (menu) => {
      const items = menuItemsFromMenu(menu, {
        branchId,
        orderType,
        limit: menu.items.length,
      }).data;
      const byId = new Map(items.map((item) => [item.id, item]));
      return (menu.checkoutSuggestionItemIds ?? [])
        .map((id) => byId.get(id))
        .filter((item): item is (typeof items)[number] => Boolean(item));
    },
  });

export const menuItemQuery = (
  slug: string,
  branchId?: string,
  orderType?: "delivery" | "pickup",
) =>
  queryOptions({
    queryKey: ["menu-item", slug, branchId, orderType],
    queryFn: ({ signal }) =>
      getMenuItemBySlug(slug, branchId, orderType, signal),
    enabled: Boolean(slug),
    refetchInterval: (query) =>
      saleRefreshInterval(query.state.data?.nextSaleChangeAt),
    ...catalogueFreshness,
  });

export const deliveryAreasQuery = () =>
  queryOptions({
    queryKey: ["delivery-areas"],
    queryFn: ({ signal }) => getDeliveryAreas(signal),
    staleTime: 60_000,
  });

export const ordersQuery = (customerId: string) =>
  queryOptions({
    queryKey: ["customer-orders", customerId],
    queryFn: ({ signal }) => listOrders(signal),
    enabled: Boolean(customerId),
    staleTime: 15_000,
  });

export const customerOrderQuery = (customerId: string, orderId: string) =>
  queryOptions({
    queryKey: ["customer-order", customerId, orderId],
    queryFn: ({ signal }) => getOrder(orderId, signal),
    enabled: Boolean(customerId && orderId),
  });

export const loyaltyWalletQuery = (customerId: string) =>
  queryOptions({
    queryKey: ["customer-loyalty", customerId],
    queryFn: ({ signal }) => storefrontSdk.loyaltyWallet({ signal }),
    enabled: Boolean(customerId),
    staleTime: 15_000,
  });
